import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { toast } from "@/components/ui/toast";
import { analyticsService } from "../../services/analytics";
import { getClientById, getUserById, useCampaignBookings, useCampaignById, usePitchesByCampaignId } from "../../services/firebase";
import type { Client, User } from "../../shared/types";

/** Load the campaign, its pitches and bookings, its booker, and the booker's client. */
export function useCampaignDetails() {
    const { campaignId } = useParams<{ campaignId: string }>();
    const { campaign, loading, error: campaignError } = useCampaignById(campaignId);
    const { pitches, loading: pitchesLoading, error: pitchesError } = usePitchesByCampaignId(campaignId);
    const { bookings, loading: bookingsLoading, error: bookingsError } = useCampaignBookings(campaignId);
    const [client, setClient] = useState<Client>();
    const [booker, setBooker] = useState<User>();

    useEffect(() => {
        if (!campaignError) {
            return;
        }
        console.error(campaignError);
        analyticsService().logError(campaignError);
    }, [campaignError]);

    useEffect(() => {
        if (pitchesError) {
            toast.add({
                type: "error",
                title: "Couldn’t load pitches",
                description: "Refresh and try again.",
            });
            console.error(pitchesError);
        }
    }, [pitchesError]);

    useEffect(() => {
        if (bookingsError) {
            toast.add({
                type: "error",
                title: "Couldn’t load bookings",
                description: "Refresh and try again.",
            });
            console.error(bookingsError);
        }
    }, [bookingsError]);

    useEffect(() => {
        const bookerUid = campaign?.booker?.uid;
        setClient(undefined);
        setBooker(undefined);
        if (!bookerUid) {
            return;
        }

        let cancelled = false;
        async function loadBooker() {
            try {
                const nextBooker = await getUserById(bookerUid!);
                if (cancelled) {
                    return;
                }
                setBooker(nextBooker);
                if (!nextBooker.clientId) {
                    return;
                }
                const nextClient = await getClientById(nextBooker.clientId);
                if (!cancelled) {
                    setClient(nextClient);
                }
            } catch (error) {
                console.error(error);
            }
        }

        void loadBooker();
        return () => {
            cancelled = true;
        };
    }, [campaign?.booker?.uid]);

    return { loading, campaign, campaignError, pitches, pitchesLoading, bookings, bookingsLoading, booker, client };
}
